/**
 * Surgical Safety Checklist - WHO SSC
 * WHO checklist instrument with phases and binary/conditional items
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import assessmentInstrumentService from '../../services/assessmentInstrumentService';
import hospitalCapabilityProfileService from '../../services/hospitalCapabilityProfileService';
import { useNotifications } from '../NotificationSystem';
import './WHOAssessment.css';

const INSTRUMENT_ID = 'WHO_SSC_2009';

const SurgicalSafetyAssessment = ({ organizationId, userId, userName, onComplete }) => {
  const { t, i18n } = useTranslation(['qms', 'common']);
  const { notify } = useNotifications();
  const [instrument, setInstrument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [activePhaseIndex, setActivePhaseIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const useRo = (i18n.language || 'ro').startsWith('ro');

  useEffect(() => {
    let cancelled = false;
    assessmentInstrumentService.getInstrument(INSTRUMENT_ID).then((data) => {
      if (!cancelled) {
        setInstrument(data);
        setLoading(false);
      }
    }).catch((err) => {
      if (!cancelled) {
        console.error('SSC load:', err);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const handleAnswer = (itemId, value) => {
    setAnswers((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleSubmit = async () => {
    if (!organizationId || !userId) {
      notify.error(t('qms:selectOrganization') || 'Select an organization.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await hospitalCapabilityProfileService.submitChecklistAssessment(
        organizationId,
        answers,
        userId,
        userName,
        INSTRUMENT_ID
      );
      notify.success(t('qms:whoAssessments.assessmentSubmitted') || 'Assessment submitted successfully.');
      // Pass full result including instrument, phases, and answers for results view
      onComplete?.({
        ...result,
        instrument,
        phases: instrument?.phases || [],
        answers
      });
    } catch (err) {
      notify.error(err.message || t('common:saveFailed') || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="who-assessment loading">
        <p>{t('qms:loading') || 'Loading…'}</p>
      </div>
    );
  }

  if (!instrument) {
    return (
      <div className="who-assessment error">
        <p>{t('qms:whoAssessments.instrumentNotFound') || 'Assessment instrument not found. Please contact your administrator.'}</p>
      </div>
    );
  }

  const phases = instrument.phases || [];
  const activePhase = phases[activePhaseIndex];
  const totalItems = phases.reduce((s, p) => s + (p.items?.length || 0), 0);
  const completedItems = Object.keys(answers).filter((k) => answers[k] !== undefined && answers[k] !== null && answers[k] !== '').length;
  const canSubmit = completedItems >= totalItems;
  const completionPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  return (
    <div className="who-assessment who-checklist">
      <div className="who-assessment-header-row">
        <div className="who-assessment-header-titles">
          <h2>{useRo ? (instrument.nameRo || instrument.name) : (instrument.name || instrument.nameRo)}</h2>
          {(instrument.descriptionRo || instrument.description) && (
            <p className="who-assessment-header-subtitle">
              {useRo ? (instrument.descriptionRo || instrument.description) : (instrument.description || instrument.descriptionRo)}
            </p>
          )}
        </div>
        <div className="who-assessment-header-meta">
          <span className="who-assessment-pill" aria-label={t('qms:whoAssessments.itemsCompleted')}>
            {completedItems} / {totalItems} {useRo ? 'completate' : 'completed'} — {completionPercentage.toFixed(0)}%
          </span>
          <div className="who-assessment-submit-wrap">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
            >
              {submitting ? (t('qms:saving') || 'Saving…') : (t('qms:whoAssessments.submitAssessment') || 'Submit Assessment')}
            </button>
          </div>
        </div>
      </div>

      <div className="who-assessment-with-sidebar">
        <nav className="who-assessment-sidebar" aria-label={t('qms:whoAssessments.phaseBreakdown') || 'Phases'}>
          <div className="who-assessment-sidebar-nav">
            {phases.map((phase, idx) => {
              const phaseItems = phase.items || [];
              const phaseCompleted = phaseItems.filter((item) => answers[item.id] !== undefined && answers[item.id] !== null && answers[item.id] !== '').length;
              const isCompleted = phaseItems.length > 0 && phaseCompleted === phaseItems.length;
              return (
                <button
                  key={phase.id}
                  type="button"
                  className={`who-assessment-sidebar-item ${idx === activePhaseIndex ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                  onClick={() => setActivePhaseIndex(idx)}
                >
                  <span className="who-assessment-sidebar-item-name">
                    {phase.code}. {useRo ? (phase.nameRo || phase.nameEn) : (phase.nameEn || phase.nameRo)}
                  </span>
                  <span className="who-assessment-sidebar-item-count">
                    {phaseCompleted} / {phaseItems.length}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <main className="who-assessment-main">
          {activePhase && (
            <>
              <div className="who-assessment-chapter-divider">
                <span className="who-assessment-chapter-badge">{activePhase.code}</span>
                <span className="who-assessment-chapter-line" />
              </div>
              {(activePhase.items || []).map((item) => {
                const defaultOptions = [
                  { value: 'yes', label: 'Yes', labelRo: 'Da' },
                  { value: 'no', label: 'No', labelRo: 'Nu' },
                  { value: 'na', label: 'N/A', labelRo: 'N/A' }
                ];
                const itemOptions = (item.options && item.options.length > 0) ? item.options : defaultOptions;
                return (
                  <div key={item.id} className="who-assessment-question-card">
                    <div className="who-assessment-question-inner">
                      <span className="who-assessment-question-code">{item.code}</span>
                      <p className="who-assessment-question-text">
                        {useRo ? (item.textRo || item.textEn) : (item.textEn || item.textRo)}
                      </p>
                    </div>
                    <div className="item-options">
                      {itemOptions.map((opt) => {
                        const optionLabel = useRo ? (opt.labelRo || opt.label || 'N/A') : (opt.label || opt.labelRo || 'N/A');
                        return (
                          <label key={opt.value} className="item-option">
                            <input
                              type="radio"
                              name={item.id}
                              value={opt.value}
                              checked={answers[item.id] === opt.value}
                              onChange={() => handleAnswer(item.id, opt.value)}
                            />
                            <span className="option-label-text">{optionLabel}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default SurgicalSafetyAssessment;
